import { OnInit, DOCUMENT, inject } from '@angular/core';
import { Component, OnDestroy } from '@angular/core';
import { Router,NavigationStart } from '@angular/router';
// Okta migration: removed MSAL imports
import { NotificationManagementService } from './services/notification-management.service';
import { AnnouncementToastService } from './services/announcement-toast.service';
import { AuthService } from './services/auth.service'; 
import { environment } from 'src/environments/environment';
import { DomSanitizer } from '@angular/platform-browser';
import { ResolutionCodingService } from './services/resolution-coding.service';
import {catchError, retry, shareReplay } from 'rxjs/operators';
import { filter, take, takeUntil } from 'rxjs/operators';
import { Subject, EMPTY } from 'rxjs';
import { OktaAuthStateService } from '@okta/okta-angular';
import { OKTA_CONFIG } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { ROUTE_PATHS } from './route-paths';
import { InactivityService } from './services/inactivity.service';
import { clearLegacyAppLocalStorage } from './services/okta-logout';

import * as Chart  from 'chart.js';
import * as ChartDataLabels from 'chartjs-plugin-datalabels';


@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  router = inject(Router);
  private oktaAuthStateService = inject(OktaAuthStateService);
  private oktaConfig = inject(OKTA_CONFIG);
  private authService = inject(AuthService);
  private sanitizer = inject(DomSanitizer);
  private resolutionCodingService = inject(ResolutionCodingService);
  private notificationManagementService = inject(NotificationManagementService);
  private announcementToastService = inject(AnnouncementToastService);
  private document = inject<Document>(DOCUMENT);
  private inactivityService = inject(InactivityService);

  title = 'anigent-ui';
  isTokenRefreshed: any
  skinPath = environment.assets;
  logoPath = "/assets/images/all_logos/" + environment.logoFile;
  skinCssUrl: any;
  showInactivityWarning = false;
  inactivityRemainingSeconds = 5 * 60;
  isIframe = false;
  isSignedOutRoute = false;
  private readonly _destroying$ = new Subject<void>();

  displayPosition!: boolean;
  showRecentlyDeleted = false;
  position!: string;
  userNotifications:any = [];
  notification: any = [];
  recentlyDeleted: any = [];
  notificationCount: any;
  loginDisplay:any = {}
  failed = false;
  loggedin = false;
  chartJs = Chart;
  chartLabelPlugin = ChartDataLabels;

  constructor() {
    this.skinCssUrl = this.sanitizer.bypassSecurityTrustResourceUrl(environment.assets + '/css/skin.css');
  }

  userNotifactions: any = [];

  ngOnInit(): void {
    clearLegacyAppLocalStorage();

    this.authService.localSessionAuthenticated.pipe(
      takeUntil(this._destroying$)
    ).subscribe(authenticated => {
      if (authenticated) {
        this.loggedin = true;
        this.inactivityService.start();
        this.resolutionCodingService.init();
        this.announcementToastService.start();
      } else if (this.authService.hasLocalSession()) {
        this.loggedin = false;
        this.inactivityService.stop();
        this.announcementToastService.stop();
      }
    });

    if (this.authService.hasLocalSession()) {
      void this.authService.initializeLocalSession();
    }

    // Listen for Okta auth state and set loggedin when authenticated
    this.oktaAuthStateService.authState$.pipe(
      filter(authState => !!authState.isAuthenticated),
      take(1),
      takeUntil(this._destroying$)
    ).subscribe(() => {
      if (this.authService.hasLocalSession()) {
        return;
      }
      this.loggedin = true;
      this.inactivityService.start();
      this.setLocalUserAccount();
    });

    this.oktaAuthStateService.authState$.pipe(
      filter(authState => authState.isAuthenticated === false),
      takeUntil(this._destroying$)
    ).subscribe(() => {
      if (this.authService.hasLocalSession()) {
        return;
      }
      this.loggedin = false;
      this.authService.clearCurrentTabSession();
      this.inactivityService.stop();
      this.announcementToastService.stop();
    });

    this.inactivityService.warningVisible$.pipe(
      takeUntil(this._destroying$)
    ).subscribe(visible => {
      this.showInactivityWarning = visible;
    });

    this.inactivityService.remainingSeconds$.pipe(
      takeUntil(this._destroying$)
    ).subscribe(remainingSeconds => {
      this.inactivityRemainingSeconds = remainingSeconds;
    });

    /**MP-116 */
    this.router.events.pipe(takeUntil(this._destroying$)).subscribe(e => {
      if (e instanceof NavigationStart) {
       const currenturl=e.url;
       this.isSignedOutRoute = currenturl.startsWith('/signed-out') || currenturl.startsWith('/security-login');
       if(!currenturl.includes(`/${ROUTE_PATHS.assayManagement}`)){
        this.authService.deleteWitnesToken();
       }

       // Track SPA route changes in Pendo
       if (typeof pendo !== 'undefined') {
        pendo.pageLoad();
       }
      }
    });
  }

  ngOnDestroy(): void {
    this.inactivityService.stop();
    this.announcementToastService.stop();
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  continueWorking(): void {
    this.inactivityService.continueWorking();
  }

  logoutCurrentTab(): void {
    this.inactivityService.logout();
  }

  setLocalUserAccount() {
    this.authService.getUserAccount()
    .pipe(
      retry(3),
      catchError(()=>{
        this.failed = true
        return EMPTY
      }),
      shareReplay()
    )
    .subscribe( (result: any) => {
      this.authService.setUser(result);
      this.resolutionCodingService.init();
      this.loggedin = true
      this.announcementToastService.start();
      this.router.navigateByUrl("")

      // Initialize Pendo with visitor and account data
      if (typeof pendo !== 'undefined' && !this.authService.pendoInitialized) {
        this.authService.setPendoInitialized(true);
        const customerIdFromUrl = window.location.hostname.split('.')[0];
        pendo.initialize({
          visitor: {
            id: result.email,
            email: result.email,
            full_name: result.fullname,
            role: result.role,
            is_demo_user: result.deIdUser,
            last_login: result.lastLogin,
            access_level: result.accessLevel,
            has_variance_module: result.modules?.variance,
            has_waste_module: result.modules?.waste,
            has_p2p_module: result.modules?.p2p,
            has_auditor_module: result.modules?.auditor,
            has_insights_module: result.modules?.insights,
            is_read_only: result.readOnly,
            regions: Array.isArray(result.regions) ? result.regions.join(',') : result.regions
          },
          account: {
            id: customerIdFromUrl,
            name: customerIdFromUrl
          }
        });
      }
    })
  }


  showPositionDialog(position: string) {
    this.position = position;
    this.displayPosition = true;
  }


  getNotifications() {
    this.notificationManagementService.getNotifications().subscribe((response) => {
      if((response as any)[0])
      this.userNotifactions = (response as any)[0].notifications
      this.notification = (response as any)[0].notifications.filter((elem: any) => elem.deleted == false)
      this.recentlyDeleted = (response as any)[0].notifications.filter((elem: any) => elem.deleted == true)
      this.notificationCount = this.notification.length;
    })
  }

  onShowModel() {
    this.document.body.classList.add("ovrhidden");
  }

  hideNotification(notification: any) {
    notification.deleted = true;
    this.notificationManagementService.updateNotification(this.userNotifactions).subscribe(() => {
      this.getNotifications();
    })
  }

  setSeen($event: any, notification: any) {
    if($event.srcElement.checked) {
      this.notificationCount = this.notificationCount - 1;
      notification.seen = true;
    } else {
      this.notificationCount = this.notificationCount + 1;
      notification.seen = false;
    }
  }

  setLoginDisplay() {
    const oktaAuth: OktaAuth = this.oktaConfig.oktaAuth;
    oktaAuth.isAuthenticated().then(auth => {
      this.loginDisplay = auth;
    });
  }

  getUserRole() {
    if (this.authService.user === undefined || this.authService.user === null){
      return ''
    }
    else {return this.authService.user.role;
    }
  }
  getLogo() {
    if (environment.logoFile){
      return "assets/images/all_logos" + environment.logoFile
    }
    else{
      return "assets/images/all_logos/logo_MAAP-Logon.png"
    }
    
  }
}
